import { StatusBadgeVariants } from 'ui/badge/constants';
import { MoveToFolder } from 'ui/icons';
import {
  AmazonAthena,
  AmazonS3,
  AzureBlob,
  AzureSynapse,
  BigQuery,
  Clickhouse,
  Databricks,
  Elasticsearch,
  Firebolt,
  GoogleCloudStorage,
  Materialize,
  MicrosoftFabric,
  MicrosoftSQLServer,
  MongoDB,
  MySQL,
  Oracle,
  PlanetScale,
  Postgres,
  Redshift,
  Rockset,
  SingleStore,
  Snowflake,
  TeradataVantage,
  Trino,
} from 'ui/thirdPartyLogos';
import { pxToRem } from 'utils/convert';
import { SourceDetail, Status } from './types';

/**
 * Common
 */
export const DATA_WAREHOUSE_URL = '/integrations/smartIngest';
export const PAGE_TITLE = 'Smart Ingest';

/**
 * Syncs
 */
export const SYNC_TABLE_GRID_LAYOUT = `
    minmax(40px, 1fr) // name
    minmax(40px, 1fr) // status
    minmax(40px, 1fr) // lastRunAt
    minmax(40px, 1fr) // schedule
    ${pxToRem(40)} // actions
`;

export const SYNC_TABLE_COLUMNS = ['Name', 'Status', 'Last run time', 'Sync schedule', ''];

export const STATUS_FILTER_OPTIONS = [
  { id: 'title', label: 'Status', isTitle: true },
  { id: 'healthy', label: 'Healthy' },
  { id: 'inProgress', label: 'In progress' },
  { id: 'warning', label: 'Warning' },
  { id: 'error', label: 'Error' },
  { id: 'disabled', label: 'Disabled' },
];

/**
 * Hightouch Status to Iterable status mapping
 *
 * |---------------|----------------------|-----------------|
 * | Hightouch API | Hightouch human name | Iterable status |
 * |---------------|----------------------|-----------------|
 * | success       | Healthy              | Healthy         |
 * | warning       | Warning              | Warning         |
 * | failed        | Error                | Error           |
 * | queued        | In Progress          | In Progress     |
 * | querying      | In Progress          | In Progress     |
 * | processing    | In Progress          | In Progress     |
 * | reporting     | In Progress          | In Progress     |
 * | interrupted   | In Progress          | In Progress     |
 * | disabled      | Inactive             | Disabled        |
 * | cancelled     | Unknown              | Cancelled       |
 * | pending       | Unknown              | Pending         |
 * |---------------|----------------------|-----------------|
 */

export const FILTER_MAPPINGS = {
  [Status.Cancelled]: {
    // These values provide the correct visual display
    // but their status doesn't coincide with the display status
    // ie. cancelled !== finished
    badgeStatus: StatusBadgeVariants.FINISHED,
    id: 'cancelled',
    label: 'Cancelled',
  },
  [Status.Disabled]: {
    badgeStatus: StatusBadgeVariants.FINISHED,
    id: 'disabled',
    label: 'Disabled',
  },
  [Status.Failed]: {
    badgeStatus: StatusBadgeVariants.ABORTED,
    id: 'error',
    label: 'Error',
  },
  [Status.Interrupted]: {
    badgeStatus: StatusBadgeVariants.RUNNING,
    id: 'inProgress',
    label: 'In progress',
  },
  [Status.Pending]: {
    badgeStatus: StatusBadgeVariants.FINISHED,
    id: 'pending',
    label: 'Pending',
  },
  [Status.Processing]: {
    badgeStatus: StatusBadgeVariants.RUNNING,
    id: 'inProgress',
    label: 'In progress',
  },
  [Status.Querying]: {
    badgeStatus: StatusBadgeVariants.RUNNING,
    id: 'inProgress',
    label: 'In progress',
  },
  [Status.Queued]: {
    badgeStatus: StatusBadgeVariants.RUNNING,
    id: 'inProgress',
    label: 'In progress',
  },
  [Status.Reporting]: {
    badgeStatus: StatusBadgeVariants.RUNNING,
    id: 'inProgress',
    label: 'In progress',
  },
  [Status.Success]: {
    badgeStatus: StatusBadgeVariants.RUNNING,
    id: 'healthy',
    label: 'Healthy',
  },
  [Status.Warning]: {
    badgeStatus: StatusBadgeVariants.DRAFT,
    id: 'warning',
    label: 'Warning',
  },
};

/**
 * Event listeners
 * https://hightouch.notion.site/Hightouch-Partner-API-f8dc92400474429cbea36bc975266693#1ac661395d3f4f17bf2f09a55b0977d3
 */
export const CROSS_ORIGIN_EVENTS = {
  createSourceStarted: 'create_source_started',
  createSourceFinished: 'create_source_finished',
  createSyncStarted: 'create_sync_started',
  createSyncFinished: 'create_sync_finished',
  windowClosed: 'window_closed',
};

export const HIGHTOUCH_ORIGIN = 'https://app.hightouch.com';

/**
 * Values used to identify sources and provide additional details like
 * readable name, icon, etc.
 *
 * Sources with `isHidden: true` property will not be shown in the UI
 * Sources will be filtered out based on the `allowedSourceTypes` endpoint
 *
 * Refers to catalog list of sources
 * https://api.hightouch.com/catalog
 *
 * List of available sources referenced in Hightouch Partner API in Creating Syncs section
 * https://hightouch.notion.site/Hightouch-Partner-API-f8dc92400474429cbea36bc975266693
 *
 * Displayed in order of list below. If more explicit order is needed, add a property
 * to assign a sortIndex and create a sorting function to accompany
 */
export const SOURCES_LIST: { [key: string]: SourceDetail } = {
  athena: {
    Icon: AmazonAthena,
    id: 'athena',
    isHidden: false,
    name: 'Amazon Athena',
  },
  'azure-blob': {
    Icon: AzureBlob,
    id: 'azure-blob',
    isHidden: false,
    name: 'Azure Blob',
  },
  bigquery: {
    Icon: BigQuery,
    id: 'bigquery',
    name: 'Google BigQuery',
  },
  clickhouse: {
    Icon: Clickhouse,
    id: 'clickhouse',
    isHidden: false,
    name: 'ClickHouse',
  },
  databricks: {
    Icon: Databricks,
    id: 'databricks',
    name: 'Databricks',
  },
  elasticsearch: {
    Icon: Elasticsearch,
    id: 'elasticsearch',
    isHidden: false,
    name: 'Elasticsearch',
  },
  fabric: {
    Icon: MicrosoftFabric,
    id: 'fabric',
    isHidden: false,
    name: 'Microsoft Fabric',
  },
  firebolt: {
    Icon: Firebolt,
    id: 'firebolt',
    isHidden: false,
    name: 'Firebolt',
  },
  gcs: {
    Icon: GoogleCloudStorage,
    id: 'gcs',
    isHidden: false,
    name: 'Google Cloud Storage',
  },
  materialize: {
    Icon: Materialize,
    id: 'materialize',
    isHidden: false,
    name: 'Materalize',
  },
  mongodb: {
    Icon: MongoDB,
    id: 'mongodb',
    isHidden: false,
    name: 'MongoDB',
  },
  mssql: {
    Icon: MicrosoftSQLServer,
    id: 'mssql',
    isHidden: false,
    name: 'Microsoft SQL Server',
  },
  mysql: {
    Icon: MySQL,
    id: 'mysql',
    isHidden: false,
    name: 'MySQL',
  },
  oracle: {
    Icon: Oracle,
    id: 'oracle',
    isHidden: false,
    name: 'Oracle DB',
  },
  planetscale: {
    Icon: PlanetScale,
    id: 'planetscale',
    isHidden: false,
    name: 'PlanetScale',
  },
  postgres: {
    Icon: Postgres,
    id: 'postgres',
    isHidden: false,
    name: 'Postgres',
  },
  redshift: {
    Icon: Redshift,
    id: 'redshift',
    name: 'Amazon Redshift',
  },
  rockset: {
    Icon: Rockset,
    id: 'rockset',
    isHidden: false,
    name: 'Rockset',
  },
  'single-store': {
    Icon: SingleStore,
    id: 'single-store',
    isHidden: false,
    name: 'SingleStore',
  },
  s3: {
    Icon: AmazonS3,
    id: 's3',
    isHidden: false,
    name: 'Amazon S3',
  },
  sftp: {
    Icon: MoveToFolder,
    id: 'sftp',
    isHidden: false,
    name: 'SFTP',
  },
  snowflake: {
    Icon: Snowflake,
    id: 'snowflake',
    name: 'Snowflake',
  },
  'teradata-vantage': {
    Icon: TeradataVantage,
    id: 'teradata-vantage',
    isHidden: false,
    name: 'Teradata Vantage',
  },
  synapse: {
    Icon: AzureSynapse,
    id: 'synapse',
    isHidden: false,
    name: 'Azure Synapse',
  },
  trino: {
    Icon: Trino,
    id: 'trino',
    isHidden: false,
    name: 'Trino',
  },
};

/**
 * Demo source provided by endpoint that should be filtered out
 */
export const DEMO_SOURCE = {
  sourceId: 53,
  name: 'Demo Database',
  type: 'postgres',
};

export enum ExitModalVariants {
  CREATE_SOURCE = 'createSource',
  CREATE_SYNC = 'createSync',
}
